import * as React from "react";
import * as S from "@styled";

// tslint:disable-next-line:no-var-requires
import { connect } from "react-redux";
import { DispatchType } from "../../../shared/utilities";
import * as LogLevelAction from "../../actions/LogLevelActions";
import { LogLevel, LogLevelIndices, LogLevelSetting, LogLevelTimeLimit } from "../../model/LogLevelSetting";
import { State } from "../../model/State";

import { ChangeEvent } from "../../../shared/model/Event";
import CustomModal from "shared/components/CustomModal";
import CustomSwitch, { ICustomSwitch } from "shared/components/CustomSwitch";
import { Checkbox } from "shared/components/Checkbox";
import Time from "shared/Time";

const LogLevelSettingsDialog: React.FC<LogLevelSettingsProps> = (props) => {
    const handleOnHide = () => props.onCloseDialog();

    const handleSetLogLevel = (newLogLevel: LogLevel) => {
        if (!props.isForProcesses && newLogLevel === null) {
            return; // do not allow setting null value on agent group log level
        }
        const currentLogLevelIndex = LogLevelIndices.findIndex((f) => f === props.logLevel);
        const incomingLogLevelIndex = LogLevelIndices.findIndex((f) => f === newLogLevel);

        // When turning ErrorsWithoutStepLogging to no.
        // We do not want to show the warning as that would confuse the user
        // The user does not see this setting as a log level but as a separate configuration
        const switchingPromotedLoggingToStepsOn = props.logLevel === "ErrorsWithoutStepLogging" && newLogLevel === "Error";
        if (!props.isForProcesses && !switchingPromotedLoggingToStepsOn && incomingLogLevelIndex > currentLogLevelIndex) {
            if (!confirm("Are you sure you want to change Agent group log level? This can cause a high load on the Agent.")) {
                return;
            }
        }
        props.onSetLogLevel(newLogLevel);
    };

    const handleSetDebugTimeLimit = (timelimit: any) => {
        if (!props.isForProcesses || timelimit === null) {
            return; // do not allow setting tme limit on agent group level
        }
        props.onDebugExpiresInChanged(timelimit);
    };

    const handleTurnOffDebugTimeLimit = () => {
        props.onDebugExpiresInChanged(0);
    };

    const handleLogLevelCheckboxToggle = (value: ChangeEvent<HTMLInputElement>) => {
        const boolValue: boolean = value.target.checked;
        if (boolValue) {
            props.onSetLogLevel(null);
        } else {
            props.onSetLogLevel(props.agentGroupSettings?.logLevel !== "Debug" ? props.agentGroupSettings?.logLevel : "Info");
        }
    };

    const handleHistoryChange = (value: ChangeEvent<HTMLInputElement>) => {
        const stringValue = value.target.value;
        const parsedValue = parseInt(stringValue, 10);
        if (isNaN(parsedValue) || parsedValue === 0) {
            props.onHistoryStorageDaysChanged();
        } else {
            props.onHistoryStorageDaysChanged(parsedValue);
        }
    };

    const handleHistoryToggle = (value: ChangeEvent<HTMLInputElement>) => {
        const boolValue = value.target.checked;
        if (boolValue) {
            props.onHistoryStorageDaysChanged(null);
        } else {
            props.onHistoryStorageDaysChanged(props.agentGroupSettings?.historyStorageDays);
        }
    };

    const handleToggleLogRequestResponse = (value?: boolean) => {
        if (!props.isForProcesses && value == null) {
            return; // do not set null value for agent group
        }
        props.onToggleLogRequestResponse(value);
    };

    const handleLogRequestResponseCheckboxToggle = (value: ChangeEvent<HTMLInputElement>) => {
        const boolValue: boolean = value.target.checked;
        if (boolValue) {
            props.onToggleLogRequestResponse(null);
        } else {
            props.onToggleLogRequestResponse(props.agentGroupSettings?.logTriggerRequestAndResponse);
        }
    };

    const getLogLevelDisabledExplanation = (): string | undefined => {
        if (!props.isForProcesses && !props.allowLogEverything) {
            return "Log Everything is disabled for this Agent group. Please contact support@frends.com for more information.";
        }
        if (props.isForProcesses && props.logLevel != null) {
            return "Log Everything can not be set for Processes";
        }
        return undefined;
    }

    const getLogLevelDescription = (logLevel: LogLevel | undefined): string => {
        // Process LogLevel is not set use the Agent Group setting instead
        const sanitizedLogLevel = logLevel ? logLevel : props.agentGroupSettings?.logLevel;
        switch (sanitizedLogLevel) {
            case "Info":
                return FRENDS.Localizations.LoggingLevel_Info_Description;
            case "Error":
            case "ErrorsWithoutStepLogging":
                return FRENDS.Localizations.LoggingLevel_Error_Description;
            case "Debug":
                return FRENDS.Localizations.LoggingLevel_Debug_Description;
            default:
                throw Error(`Log level '${logLevel}' does not exist`);
        }
    };

    const showDisablePromotedSteplogging = () => {
        if (props.logLevel !== null) {
            return props.logLevel === "Error" || props.logLevel === "ErrorsWithoutStepLogging";
        }
        return props.agentGroupSettings?.logLevel === "Error" || props.agentGroupSettings?.logLevel === "ErrorsWithoutStepLogging";
    };

    const storageDaysValue = props.historyStorageDays || props.agentGroupSettings?.historyStorageDays || 0;

    const isLogLevelSelected = (logLevel: LogLevel, additionalLevel?: LogLevel) => {
        if (props.logLevel !== null || props.logLevel !== undefined) {
            return props.logLevel === logLevel || props.logLevel === additionalLevel;
        }
        return props.agentGroupSettings?.logLevel === logLevel || props.agentGroupSettings?.logLevel === additionalLevel;
    }

    const logLevelSwitches: ICustomSwitch[] = [
        {
            text: FRENDS.Localizations.LoggingLevel_Info,
            onClick: () => handleSetLogLevel("Info"),
            selected: isLogLevelSelected("Info"),
            disabled: props.logLevel === null
        },
        {
            text: FRENDS.Localizations.LoggingLevel_Error,
            onClick: () => handleSetLogLevel("Error"),
            selected: isLogLevelSelected("Error", "ErrorsWithoutStepLogging"),
            disabled: props.logLevel === null
        },
        {
            text: FRENDS.Localizations.LoggingLevel_Debug,
            onClick: () => handleSetLogLevel("Debug"),
            selected: isLogLevelSelected("Debug"),
            disabled: props.logLevel === null || !props.allowLogEverything || props.isForProcesses,
            title: getLogLevelDisabledExplanation()
        }
    ];

    const instanceLogLevelSwitches: ICustomSwitch[] = [
        {
            text: FRENDS.Localizations.Yes,
            onClick: () => handleSetLogLevel("ErrorsWithoutStepLogging"),
            selected: props.logLevel === "ErrorsWithoutStepLogging",
            disabled: props.logLevel === null
        },
        {
            text: FRENDS.Localizations.No,
            onClick: () => handleSetLogLevel("Error"),
            selected: props.logLevel === "Error",
            disabled: props.logLevel === null
        }
    ];

    const logRequestAndRequestSwitches: ICustomSwitch[] = [
        {
            selected: !!(props.logRequestResponse !== null ? props.logRequestResponse : props.agentGroupSettings?.logTriggerRequestAndResponse),
            onClick: () => handleToggleLogRequestResponse(true),
            text: FRENDS.Localizations.Yes
        },
        {
            disabled: props.logRequestResponse === null,
            selected: !(props.logRequestResponse !== null ? props.logRequestResponse : props.agentGroupSettings?.logTriggerRequestAndResponse),
            onClick: () => handleToggleLogRequestResponse(false),
            text: FRENDS.Localizations.No
        }
    ];

    const debugExpiresInSecondsSwitches: ICustomSwitch[] = [
        {
            onClick: () => handleTurnOffDebugTimeLimit(),
            text: FRENDS.Localizations.TurnOff,
            selected: props.setDebugeExpiresInSeconds === LogLevelTimeLimit.Off
        },
        {
            onClick: () => handleSetDebugTimeLimit(LogLevelTimeLimit.Minutes15),
            text: "15 " + FRENDS.Localizations.Minutes.toLowerCase(),
            selected: props.setDebugeExpiresInSeconds === LogLevelTimeLimit.Minutes15
        },
        {
            onClick: () => handleSetDebugTimeLimit(LogLevelTimeLimit.Minutes60),
            text: "1 " + FRENDS.Localizations.Hour.toLowerCase(),
            selected: props.setDebugeExpiresInSeconds === LogLevelTimeLimit.Minutes60
        },
        {
            onClick: () => handleSetDebugTimeLimit(LogLevelTimeLimit.Hours4),
            text: "4 " + FRENDS.Localizations.Hours.toLowerCase(),
            selected: props.setDebugeExpiresInSeconds === LogLevelTimeLimit.Hours4
        },
        {
            onClick: () => handleSetDebugTimeLimit(LogLevelTimeLimit.Hours24),
            text: "24 " + FRENDS.Localizations.Hours.toLowerCase(),
            selected: props.setDebugeExpiresInSeconds === LogLevelTimeLimit.Hours24
        }
    ];

    if (!props.isVisible) {
        return null;
    }

    return (
        <CustomModal
            label="log-settings-modal"
            onClose={handleOnHide}
            size={"medium"}
            show={true}
            shouldCloseOnOverlayClick={true}
            modalTitle={<h2>{props.isForProcesses ? "Log settings for selected Processes" : "Default log settings for all processes in Agent group"}</h2>}
        >
            <S.FormGroup>
                <S.Label>{FRENDS.Localizations.LogLevel}</S.Label>
                {props.isForProcesses ? (
                    <S.FlexContainer style={{ marginBottom: "5px" }}>
                        <Checkbox checked={props.logLevel === null} onChange={handleLogLevelCheckboxToggle} />
                        <S.Label>Use Agent group setting</S.Label>
                    </S.FlexContainer>
                ) : null}
                <CustomSwitch
                    switches={logLevelSwitches}
                />
                <p>{getLogLevelDescription(props.logLevel)}</p>
            </S.FormGroup>
            {showDisablePromotedSteplogging() ?
                <S.FormGroup id="disablePromotedResultLoggingToggles">
                    <S.Label>Disable promoted result logging to instance details</S.Label>
                    <CustomSwitch
                        switches={instanceLogLevelSwitches}
                    />
                    {!props.isForProcesses ? <p>Processes built with frends version 5.4 or higher support this feature.</p> : null}
                    {props.selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging === false ? (
                        <S.WarningContainer>
                            <div className="alert-warning" id="promotedResultLoggingNotCompatible">
                                <div className="alert-label">Selected Processes do not support this feature. Please re-build your Processes if you want this setting to take effect.</div>
                            </div>
                        </S.WarningContainer>
                    ) : null}
                </S.FormGroup> : null}
            {props.disableSetDebugLogging ? null :
                <S.FormGroup>
                    {props.debugWillExpireAtDateTime ? (
                        <S.WarningContainer>
                            <div className="alert-warning">
                                <div className="alert-label">{FRENDS.Localizations.EverythingLoggedUntil} {props.debugWillExpireAtDateTime}</div>
                                <S.Button onClick={handleTurnOffDebugTimeLimit}>{FRENDS.Localizations.TurnOff}</S.Button>
                            </div>
                        </S.WarningContainer>
                    ) : (
                        <>
                            <S.Label>{FRENDS.Localizations.LogEverythingFor}</S.Label>
                            <CustomSwitch
                                switches={debugExpiresInSecondsSwitches}
                            />
                        </>
                    )}
                </S.FormGroup>}
            <S.FormGroup>
                <S.Label>{FRENDS.Localizations.LogTriggerData}</S.Label>
                {props.isForProcesses ? (
                    <S.FlexContainer style={{ marginBottom: "5px" }}>
                        <Checkbox checked={props.logRequestResponse === null} onChange={handleLogRequestResponseCheckboxToggle} />
                        <S.Label>Use Agent group setting</S.Label>
                    </S.FlexContainer>
                ) : null}
                <CustomSwitch
                    switches={logRequestAndRequestSwitches}
                />
            </S.FormGroup>
            <S.FormGroup>
                <S.Label>{FRENDS.Localizations.ProcessInstanceHistoryRetentionPeriod}</S.Label>
                {props.isForProcesses ? (
                    <S.FlexContainer style={{ marginBottom: "5px" }}>
                        <Checkbox checked={props.historyStorageDays === null} onChange={handleHistoryToggle} />
                        <S.Label>Use Agent group setting</S.Label>
                    </S.FlexContainer>
                ) : null}
                <S.Input
                    className="history-storage-days-selector"
                    type="number"
                    min={0}
                    max={props.isForProcesses ? props.maxHistoryStorageDaysForProcesses : props.maxHistoryStorageDaysForAgentGroups}
                    disabled={props.historyStorageDays === null}
                    value={storageDaysValue}
                    onChange={handleHistoryChange}
                />
                {props.historyStorageDays
                    ? <p>{FRENDS.Localizations.ProcessInstanceHistoryRetention_Description.replace("{0}", storageDaysValue.toString())}</p>
                    : null}
                <p>It is recommended to keep the Process Instance history retention period as low as possible. Maximum value for {props.isForProcesses ?
                    `Processes is ${props.maxHistoryStorageDaysForProcesses} days.`
                    : `Agent groups is ${props.maxHistoryStorageDaysForAgentGroups} days.`}</p>
            </S.FormGroup>
            <S.FormGroup>
                <S.ButtonGroup gap={15} $justifyContentEnd={true}>
                    <S.Button variant={S.ButtonType.bordered} onClick={props.onCloseDialog} className="cancel-log-level-selector">{FRENDS.Localizations.Cancel}</S.Button>
                    <S.Button onClick={props.onSave} className="save-log-level-selector">{FRENDS.Localizations.SaveChanges}</S.Button>
                </S.ButtonGroup>
            </S.FormGroup>
        </CustomModal>
    );
};

export type LogLevelSettingsProps = LogLevelSettingsOwnProps & LogLevelSettingsStateProps & LogLevelSettingsDispatchProps;


export interface LogLevelSettingsOwnProps { }

export interface LogLevelSettingsStateProps {
    isForProcesses?: boolean
    isVisible?: boolean;
    logLevel?: LogLevel;
    historyStorageDays?: number;
    logRequestResponse?: boolean;
    agentGroupSettings?: LogLevelSetting;
    maxHistoryStorageDaysForProcesses?: number;
    maxHistoryStorageDaysForAgentGroups?: number;
    allowLogEverything?: boolean;
    debugWillExpireAtDateTime?: string | null;
    setDebugeExpiresInSeconds?: number;
    disableSetDebugLogging?: boolean;
    selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging?: boolean | null;
}

export interface LogLevelSettingsDispatchProps {
    onSetLogLevel: (level: LogLevel | undefined) => void;
    onToggleLogRequestResponse: (value?: boolean | null) => void;
    onHistoryStorageDaysChanged: (value?: number | null) => void;
    onSave: () => void;
    onDebugExpiresInChanged: (value: number) => void;
    onCloseDialog: () => void;
}

const mapStateToProps = (state: State): LogLevelSettingsStateProps => {
    return {
        isVisible: state.logLevelDialogState.showDialog,
        isForProcesses: !state.logLevelDialogState.isAgentGroupSetting,
        allowLogEverything: state.logLevelDialogState.allowLogEverything,
        disableSetDebugLogging: state.logLevelDialogState.processIds?.length !== 1, // Only allow setting debug loggin for single Processes
        logLevel: state.logLevelDialogState.currentSettings?.logLevel, // Log level will be null if it's process setting but nothing is set
        historyStorageDays: state.logLevelDialogState.currentSettings?.historyStorageDays, // Will be null if it's process setting but nothing is set
        logRequestResponse: state.logLevelDialogState.currentSettings?.logTriggerRequestAndResponse, //Will be null if it's process setting but nothing is set
        agentGroupSettings: state.logLevelDialogState.loadedSettings?.agentGroupSettings,
        maxHistoryStorageDaysForAgentGroups: state.logLevelDialogState.loadedSettings?.maxHistoryStorageDaysForAgentGroups,
        maxHistoryStorageDaysForProcesses: state.logLevelDialogState.loadedSettings?.maxHistoryStorageDaysForProcesses,
        debugWillExpireAtDateTime: state.logLevelDialogState.currentSettings?.debugExpiresAt != null ?
            Time.toDetailedLocalTime(state.logLevelDialogState.currentSettings.debugExpiresAt) : null,
        setDebugeExpiresInSeconds: state.logLevelDialogState.currentSettings?.setDebugeExpiresInSeconds,
        selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging: state.logLevelDialogState.selectedProcessesSupportLogLevelErrorsWithDisabledPromotedResultLogging
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>): LogLevelSettingsDispatchProps => {
    return {
        onToggleLogRequestResponse: (value?: boolean) => dispatch(LogLevelAction.setLogRequestResponse(value)),
        onCloseDialog: () => dispatch(LogLevelAction.closeLogLevelSettings()),
        onSetLogLevel: (level: LogLevel) => dispatch(LogLevelAction.setLogLevel(level)),
        onHistoryStorageDaysChanged: (value?: number) => dispatch(LogLevelAction.setRetentionDays(value)),
        onDebugExpiresInChanged: (value) => dispatch(LogLevelAction.setDebugExpiresIn(value)),
        onSave: () => dispatch(LogLevelAction.saveLogLevelSettings())
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LogLevelSettingsDialog);
